import type { LucideIcon } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

export type KpiTone = 'primary' | 'info' | 'success' | 'warning' | 'destructive';

/** Static token classes (Tailwind can't see dynamic `bg-${tone}/10`). */
const TONE_CHIP: Record<KpiTone, string> = {
  primary: 'bg-primary/10 text-primary',
  info: 'bg-info/10 text-info',
  success: 'bg-success/10 text-success',
  warning: 'bg-warning/10 text-warning',
  destructive: 'bg-destructive/10 text-destructive',
};

interface StatKpiCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon?: LucideIcon;
  /** Icon-chip color (telemetry extension of the house `bg-primary/10`). Default `primary`. */
  tone?: KpiTone;
  /** Override the big value's color — e.g. amber past a threshold. */
  valueClassName?: string;
  /** Small line under the subtitle (trend / status). */
  footer?: React.ReactNode;
  /** Definition + data source, shown on hover/focus of the (dotted-underlined) title. */
  titleTip?: string;
  className?: string;
}

/**
 * System Health KPI tile (86d3peur0 Phase C) — the house `KpiCard` extended with semantic icon-chip tones
 * and a definition tooltip on the title (the page-wide "dotted underline = hoverable definition" convention).
 */
export function StatKpiCard({
  title,
  value,
  subtitle,
  icon: Icon,
  tone = 'primary',
  valueClassName,
  footer,
  titleTip,
  className,
}: StatKpiCardProps) {
  const titleEl = titleTip ? (
    <Tooltip>
      <TooltipTrigger asChild>
        <p
          tabIndex={0}
          className="w-fit cursor-help text-sm font-medium text-muted-foreground underline decoration-dotted decoration-muted-foreground/40 underline-offset-2 hover:decoration-muted-foreground"
        >
          {title}
        </p>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">{titleTip}</TooltipContent>
    </Tooltip>
  ) : (
    <p className="text-sm font-medium text-muted-foreground">{title}</p>
  );

  return (
    <Card className={className}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 space-y-1">
            {titleEl}
            <p className={cn('text-2xl font-bold', valueClassName)}>{value}</p>
            {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
            {footer && <div className="text-xs">{footer}</div>}
          </div>
          {Icon && (
            <div className={cn('shrink-0 rounded-lg p-2', TONE_CHIP[tone])}>
              <Icon className="h-5 w-5" />
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
